import React, { useState, useRef, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Shield } from "lucide-react";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@shadcn-in-nx/ui/select";
import { Input } from "@shadcn-in-nx/ui/input";
import { Label } from "@shadcn-in-nx/ui/label";

const SECURITY_TYPES = ["everyone", "logged-in", "role", "custom"];
const ROLE_OPTIONS = ["user", "admin"];
const DEBOUNCE_MS = 600;

export default function IntegrationSecuritySelect({ rule, onChange, disabled, entityFields = [] }) {
    const { t } = useTranslation();
    const [localRule, setLocalRule] = useState(rule || { type: "everyone" });
    const timerRef = useRef(null);

    const debouncedSave = useCallback((newRule) => {
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => onChange(newRule), DEBOUNCE_MS);
    }, [onChange]);

    useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

    const handleTypeChange = (type) => {
        const newRule = { type };
        if (type === "role") newRule.role = "admin";
        if (type === "custom") {
            newRule.userField = "";
            newRule.matchValue = "";
        }
        setLocalRule(newRule);
        if (type !== "custom") debouncedSave(type === "everyone" ? null : newRule);
    };

    const handleRoleChange = (role) => {
        const updated = { ...localRule, role };
        setLocalRule(updated);
        debouncedSave(updated);
    };

    const handleFieldChange = (field, value) => {
        const updated = { ...localRule, [field]: value };
        setLocalRule(updated);
        if (updated.userField && updated.matchValue) debouncedSave(updated);
    };

    return (
        <div className="space-y-2">
            <div className="flex items-center gap-1.5">
                <Shield className="h-3.5 w-3.5 text-muted-foreground" />
                <Label className="text-xs font-medium text-muted-foreground">
                    {t("integrationsPanel.security.label") || "Access"}
                </Label>
            </div>
            <Select
                value={localRule.type}
                onValueChange={handleTypeChange}
                disabled={disabled}
            >
                <SelectTrigger className="h-8 text-xs">
                    <SelectValue />
                </SelectTrigger>
                <SelectContent>
                    {SECURITY_TYPES.map((type) => (
                        <SelectItem key={type} value={type} className="text-xs">
                            {t(`integrationsPanel.security.${type}`) || type}
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>

            {localRule.type === "role" && (
                <Select
                    value={localRule.role || "admin"}
                    onValueChange={handleRoleChange}
                    disabled={disabled}
                >
                    <SelectTrigger className="h-8 text-xs">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        {ROLE_OPTIONS.map((role) => (
                            <SelectItem key={role} value={role} className="text-xs">
                                {t(`integrationsPanel.security.role_${role}`) || role}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            )}

            {localRule.type === "custom" && (
                <div className="space-y-1.5">
                    {entityFields.length > 0 ? (
                        <Select
                            value={localRule.userField || ""}
                            onValueChange={(value) => handleFieldChange("userField", value)}
                            disabled={disabled}
                        >
                            <SelectTrigger className="h-8 text-xs">
                                <SelectValue placeholder={t("integrationsPanel.security.fieldPlaceholder") || "Select a user field"} />
                            </SelectTrigger>
                            <SelectContent>
                                {entityFields.map((field) => (
                                    <SelectItem key={field} value={field} className="text-xs">
                                        {field}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    ) : (
                        <Input
                            className="h-8 text-xs"
                            placeholder={t("integrationsPanel.security.fieldPlaceholder") || "User field (e.g. department)"}
                            value={localRule.userField || ""}
                            onChange={(e) => handleFieldChange("userField", e.target.value)}
                            disabled={disabled}
                        />
                    )}
                    <Input
                        className="h-8 text-xs"
                        placeholder={t("integrationsPanel.security.valuePlaceholder") || "Match value (e.g. engineering)"}
                        value={localRule.matchValue || ""}
                        onChange={(e) => handleFieldChange("matchValue", e.target.value)}
                        disabled={disabled}
                    />
                </div>
            )}
        </div>
    );
}
